'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QualityMetric {
  id: number;
  label: string;
  value: number;
  unit: string;
  target: number;
  status: 'excellent' | 'good' | 'warning';
  icon: string;
}

const QualityMonitoring = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metrics: QualityMetric[] = [
    {
      id: 1,
      label: "Weld Pass Rate",
      value: 99.2,
      unit: "%",
      target: 98.0,
      status: "excellent",
      icon: "CheckBadgeIcon"
    },
    {
      id: 2,
      label: "First-Time Quality",
      value: 97.8,
      unit: "%",
      target: 95.0,
      status: "excellent",
      icon: "StarIcon"
    },
    {
      id: 3,
      label: "Contamination Events",
      value: 0.3,
      unit: "per 1000",
      target: 1.0,
      status: "excellent",
      icon: "ShieldCheckIcon"
    },
    {
      id: 4,
      label: "Avg. Inspection Time",
      value: 12,
      unit: "minutes",
      target: 15,
      status: "good",
      icon: "ClockIcon"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-success';
      case 'good':
        return 'text-primary';
      case 'warning':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'bg-success/10';
      case 'good':
        return 'bg-primary/10';
      case 'warning':
        return 'bg-warning/10';
      default:
        return 'bg-muted';
    }
  };

  if (!isHydrated) {
    return (
      <section className="py-14 lg:py-22 bg-card">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
              <Icon name="ChartBarIcon" size={20} variant="solid" className="text-primary" />
              <span className="text-sm font-cta font-semibold text-primary">Real-Time Monitoring</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Quality Performance Dashboard
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-background rounded-lg shadow-md p-6 border border-border animate-pulse">
                <div className="h-12 bg-muted rounded mb-4"></div>
                <div className="h-8 bg-muted rounded mb-2"></div>
                <div className="h-4 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-14 lg:py-22 bg-card">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="ChartBarIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-cta font-semibold text-primary">Real-Time Monitoring</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Quality Performance Dashboard
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Live metrics tracking our welding quality, efficiency, and contamination control performance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {metrics.map((metric) => (
            <div
              key={metric.id}
              className="bg-background rounded-lg shadow-md p-6 border border-border hover:shadow-lg transition-all duration-base"
            >
              <div className={`w-12 h-12 ${getStatusBg(metric.status)} rounded-lg flex items-center justify-center mb-4`}>
                <Icon
                  name={metric.icon as any}
                  size={24}
                  variant="solid"
                  className={getStatusColor(metric.status)}
                />
              </div>
              
              <div className="mb-2">
                <div className="flex items-baseline space-x-1">
                  <span className="text-3xl font-headline font-bold text-foreground">
                    {metric.value}
                  </span>
                  <span className="text-lg text-muted-foreground">{metric.unit}</span>
                </div>
              </div>
              
              <h3 className="text-sm font-cta font-semibold text-foreground mb-3">
                {metric.label}
              </h3>
              
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Target: {metric.target}{metric.unit}</span>
                <span className={`font-cta font-bold ${getStatusColor(metric.status)}`}>
                  {metric.value >= metric.target ? '↑' : '↓'} {Math.abs(((metric.value - metric.target) / metric.target * 100)).toFixed(1)}%
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-background rounded-lg shadow-md p-8 border border-border">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="ClipboardDocumentCheckIcon" size={24} variant="solid" className="text-success" />
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground">
                Recent Quality Audits
              </h3>
            </div>
            
            <div className="space-y-4">
              {[
                { date: "January 15, 2026", auditor: "ISO Certification Body", result: "Passed", score: "98.5%" },
                { date: "December 20, 2025", auditor: "Customer Quality Team", result: "Passed", score: "99.2%" },
                { date: "November 10, 2025", auditor: "Internal Audit", result: "Passed", score: "97.8%" }
              ].map((audit, idx) => (
                <div key={idx} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-cta font-semibold text-foreground">{audit.auditor}</p>
                    <p className="text-xs text-muted-foreground">{audit.date}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-cta font-bold text-success">{audit.score}</span>
                    <div className="px-3 py-1 bg-success/10 rounded-full">
                      <span className="text-xs font-cta font-bold text-success">{audit.result}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-background rounded-lg shadow-md p-8 border border-border">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="AcademicCapIcon" size={24} variant="solid" className="text-primary" />
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground">
                Welder Certifications
              </h3>
            </div>
            
            <div className="space-y-4">
              {[
                { name: "Michael Rodriguez", certs: "AWS D1.1, ASME IX", level: "Senior Welder", exp: "15 years" },
                { name: "Sarah Chen", certs: "ISO 3834, CWB", level: "Lead Welder", exp: "12 years" },
                { name: "James Thompson", certs: "AWS D1.1, GTAW", level: "Certified Welder", exp: "8 years" }
              ].map((welder, idx) => (
                <div key={idx} className="flex items-start space-x-4 py-3 border-b border-border last:border-0">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="UserIcon" size={24} variant="solid" className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-cta font-semibold text-foreground">{welder.name}</p>
                    <p className="text-xs text-muted-foreground mb-1">{welder.level} • {welder.exp}</p>
                    <div className="flex flex-wrap gap-1">
                      {welder.certs.split(', ').map((cert, certIdx) => (
                        <span
                          key={certIdx}
                          className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-cta font-semibold rounded"
                        >
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QualityMonitoring;